import { LOCALES, LOCALE_DIRECTION, type Locale } from '@transfers-system/contracts';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import arTiers from '../locales/ar/tiers.json';
import arHolders from '../locales/ar/holders.json';
import arTransferSettings from '../locales/ar/transferSettings.json';
import arReports from '../locales/ar/reports.json';
import enTiers from '../locales/en/tiers.json';
import enHolders from '../locales/en/holders.json';
import enTransferSettings from '../locales/en/transferSettings.json';
import enReports from '../locales/en/reports.json';
import arAuth from '../locales/ar/auth.json';
import arCommon from '../locales/ar/common.json';
import arErrors from '../locales/ar/errors.json';
import arLayout from '../locales/ar/layout.json';
import arNewTransfer from '../locales/ar/newTransfer.json';
import arPhone from '../locales/ar/phone.json';
import arReceipt from '../locales/ar/receipt.json';
import arSettings from '../locales/ar/settings.json';
import arTransfers from '../locales/ar/transfers.json';
import arValidation from '../locales/ar/validation.json';
import enAuth from '../locales/en/auth.json';
import enCommon from '../locales/en/common.json';
import enErrors from '../locales/en/errors.json';
import enLayout from '../locales/en/layout.json';
import enNewTransfer from '../locales/en/newTransfer.json';
import enPhone from '../locales/en/phone.json';
import enReceipt from '../locales/en/receipt.json';
import enSettings from '../locales/en/settings.json';
import enTransfers from '../locales/en/transfers.json';
import enValidation from '../locales/en/validation.json';

export const DEFAULT_LOCALE: Locale = 'ar';
export const STORAGE_KEY = 'app_language';
export const resources = {
  ar: {
    common: arCommon,
    auth: arAuth,
    errors: arErrors,
    layout: arLayout,
    validation: arValidation,
    settings: arSettings,
    transfers: arTransfers,
    receipt: arReceipt,
    newTransfer: arNewTransfer,
    tiers: arTiers,
    holders: arHolders,
    transferSettings: arTransferSettings,
    reports: arReports,
    phone: arPhone,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    errors: enErrors,
    layout: enLayout,
    validation: enValidation,
    settings: enSettings,
    transfers: enTransfers,
    receipt: enReceipt,
    newTransfer: enNewTransfer,
    tiers: enTiers,
    holders: enHolders,
    transferSettings: enTransferSettings,
    reports: enReports,
    phone: enPhone,
  },
} as const;
export const NAMESPACES = [
  'common',
  'auth',
  'errors',
  'layout',
  'validation',
  'settings',
  'transfers',
  'receipt',
  'newTransfer',
  'tiers',
  'holders',
  'transferSettings',
  'reports',
  'phone',
] as const;

// Arabic is the initial and fallback language (AGENTS.md); only an explicit
// saved choice overrides it, browser language is not treated as a choice.
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    ns: [...NAMESPACES],
    defaultNS: 'common',
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

/** Keep direction, typography and document identity aligned with the saved locale. */
export function applyLocale(locale: Locale): void {
  const root = document.documentElement;
  const fontFamily = locale === 'ar' ? 'Cairo, system-ui, sans-serif' : 'Inter, sans-serif';
  root.lang = locale;
  root.dir = LOCALE_DIRECTION[locale];
  root.setAttribute('data-locale', locale);
  root.style.fontFamily = fontFamily;
  if (document.body) document.body.style.fontFamily = fontFamily;
  document.title = i18n.t('appName', { ns: 'common' });
}

export async function changeLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  applyLocale(locale);
}

export function currentLocale(): Locale {
  const language = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  return (LOCALES as readonly string[]).includes(language) ? (language as Locale) : DEFAULT_LOCALE;
}
applyLocale(currentLocale());
export default i18n;
